import apiClient from "../../stateManagers/services/axiosInterceptor";


interface DownloadOptions {
  filename: string;
  endpoint?: string;
  customFilename?: string;
  onSuccess?: (filename: string) => void;
  onError?: (error: any) => void;
  onProgress?: (progress: number) => void;
}

interface DownloadResponse {
  success: boolean;
  message: string;
  error?: string;
}

class FileDownloadService {
  private defaultEndpoint = '/superadmin/download-csv';

  /**
   * Download a file from the server
   * @param options Download configuration options
   * @returns Promise with download status
   */
  async downloadFile(options: DownloadOptions): Promise<DownloadResponse> {
    const {
      filename,
      endpoint = this.defaultEndpoint,
      customFilename,
      onSuccess,
      onError,
      onProgress,
    } = options;

    try {
      // Validate filename
      if (!filename || filename.trim() === '') {
        throw new Error('Filename is required');
      }

      // Make API request
      const response = await apiClient.get(endpoint, {
        params: { filename },
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });

      // Check if response is an error (some APIs return JSON errors as blobs)
      const isJsonError = response.data.type === 'application/json';
      if (isJsonError) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Download failed');
      }

      // Create blob and download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const url = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = customFilename || filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Success callback
      if (onSuccess) {
        onSuccess(customFilename || filename);
      }

      return {
        success: true,
        message: 'File downloaded successfully',
      };
    } catch (error: any) {
      console.error('Download error:', error);

      // Error callback
      if (onError) {
        onError(error);
      }

      return {
        success: false,
        message: 'Download failed',
        error: error.response?.data?.error || error.message || 'Unknown error',
      };
    }
  }

  /**
   * Download multiple files sequentially
   * @param files Array of file configurations
   * @returns Promise with array of download results
   */
  async downloadMultipleFiles(
    files: DownloadOptions[]
  ): Promise<DownloadResponse[]> {
    const results: DownloadResponse[] = [];

    for (const fileOptions of files) {
      const result = await this.downloadFile(fileOptions);
      results.push(result);

      // Add small delay between downloads to avoid overwhelming the server
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    return results;
  }

  /**
   * Download file with automatic retry on failure
   * @param options Download configuration options
   * @param maxRetries Maximum number of retry attempts
   * @returns Promise with download status
   */
  async downloadFileWithRetry(
    options: DownloadOptions,
    maxRetries: number = 3
  ): Promise<DownloadResponse> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const result = await this.downloadFile(options);
        if (result.success) {
          return result;
        }
        lastError = result.error;
      } catch (error) {
        lastError = error;
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff)
          await new Promise((resolve) =>
            setTimeout(resolve, Math.pow(2, attempt) * 1000)
          );
        }
      }
    }

    return {
      success: false,
      message: `Download failed after ${maxRetries} attempts`,
      error: lastError,
    };
  }

  /**
   * Set custom default endpoint for downloads
   * @param endpoint New default endpoint
   */
  setDefaultEndpoint(endpoint: string): void {
    this.defaultEndpoint = endpoint;
  }
}

// Export singleton instance
export const fileDownloadService = new FileDownloadService();

// Export class for custom instances if needed
export default FileDownloadService;